import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';

import {
  Cache,
  CachedMessage,
} from '../../../../data/dietitian/domain/cache/cache';
import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import {
  Conversation,
  ConversationProps,
} from '../../../../data/message/domain/conversation/conversation';
import { Profile } from '../../../../data/message/domain/profile/profile';
import { Patient } from '../../../../data/patient/domain/patient';
import * as confirmationModalComponent from '../../confirmation-modal/confirmation-modal.component';
import { MessageModel } from '../message.models';
import { AudioRecordingService } from './audio-recording.service';
import {
  ConversationFacade,
  MessageData,
  MessageViewModel,
} from './conversation.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.scss'],
  providers: [ConversationFacade],
})
export class ConversationComponent implements OnDestroy, AfterViewChecked {
  @ViewChild('messagesList') private messagesList: ElementRef;

  private _conversationId: string;
  @Input() set conversationId(value: string) {
    this.loading = true;
    this._conversationId = value;
    if (this.viewModelSub) {
      this.loadConversation().then();
      return;
    }
    this.viewModelSub = this.viewModel$.subscribe(async (combinedStates) => {
      const expected = combinedStates.filter(
        (state) => !state.isLoading && state.dietitian,
      );
      if (expected && expected.length > 0) {
        this.dietitian = expected[0].dietitian;
        await this.loadConversation().then();
      }
    });
  }
  get conversationId(): string {
    return this._conversationId;
  }
  @Input() isBack: boolean;
  @Input() isForward: boolean;
  @Input() origin = 'list';
  @Input() dietitian: Dietitian | undefined;
  @Input() displayMarkAsReadButton = true;
  @Input() mode: 'patient' | 'dietitian' = 'dietitian';
  @Output() messageBack = new EventEmitter();
  @Output() messageForward = new EventEmitter();
  @Output() messageClose = new EventEmitter();
  @Output() messageList = new EventEmitter();
  public conversation?: Conversation;
  public messageContent: string | undefined;
  public loading = true;
  public sending = false;
  public closingConversation = false;
  public bsModalRef?: BsModalRef;
  public messages: MessageModel[];
  public dayMessages: Map<string, MessageModel[]>;
  public audioUrl?: string;
  public audioBlob?: Blob;
  public imageUrl?: string;
  private disableScrollDown = false;
  private messageSub: Subscription;
  private inputSubscription: Subscription;
  private viewModel$: Observable<MessageViewModel[]>;
  private viewModelSub: Subscription;
  private patient: Patient;
  private input$ = new Subject<string>();

  constructor(
    private modalService: BsModalService,
    private facade: ConversationFacade,
    public audioRecordingService: AudioRecordingService,
  ) {
    this.viewModel$ = combineLatest(
      this.facade.dietitianState$,
      this.facade.myDietitianState$,
    );

    this.inputSubscription = this.input$
      .pipe(debounceTime(700), distinctUntilChanged())
      .subscribe((v) => {
        this.cacheMessage(this.conversation, v).then();
      });
  }

  ngOnDestroy(): void {
    this.messageSub?.unsubscribe();
    this.viewModelSub?.unsubscribe();
    this.inputSubscription?.unsubscribe();
  }

  onInput(value: string) {
    this.messageContent = value;
    this.input$.next(value);
  }

  async loadConversation() {
    try {
      this.messageContent = undefined;
      this.conversation = await this.facade.loadConversation(
        this.conversationId,
      );

      if (this.conversation && this.conversation.patient) {
        this.patient = await this.facade.loadPatient(
          this.conversation.patient.profileId?.id?.toString(),
        );
      }
      this.loadMessages();
    } catch {
      this.conversation = undefined;
      this.messages = [];
    }
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  onMessagesScroll() {
    const element = this.messagesList.nativeElement;
    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;
    this.disableScrollDown = !(this.disableScrollDown && atBottom);
  }

  private scrollToBottom(): void {
    if (this.disableScrollDown) {
      return;
    }
    try {
      this.messagesList.nativeElement.scrollTop =
        this.messagesList.nativeElement.scrollHeight;
    } catch (err) {
      // nothing
    }
  }

  private groupConversationsByDay() {
    this.dayMessages = new Map<string, MessageModel[]>();
    for (let index = 0; index < this.messages.length; index++) {
      const conversation = this.messages[index];
      const formattedDate = moment(conversation.date)
        .startOf('day')
        .format('YYYY-MM-DD');

      if (!this.dayMessages.has(formattedDate)) {
        this.dayMessages.set(formattedDate, []);
      }

      this.dayMessages.get(formattedDate)?.push(conversation);
    }
  }

  loadMessages() {
    if (!this.conversation || !this.patient || !this.dietitian) {
      return;
    }

    // Unsubscribe if we a subscription already exists
    this.messageSub?.unsubscribe();
    this.messageSub = this.facade
      .listenMessages(
        this.conversation.conversationId.id.toString(),
        this.dietitian,
        this.patient,
      )
      .subscribe((messages) => {
        this.messages = messages;
        this.groupConversationsByDay();

        if (!this.sending) {
          this.loadCachedMessage(this.conversation)
            .then((cachedMessage) => {
              this.messageContent = cachedMessage?.message;
              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
            });
        } else {
          this.loading = false;
        }

        this.disableScrollDown = false;
        this.scrollToBottom();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async pictureSelected(event: any): Promise<void> {
    if (!event.target.files?.length) {
      return undefined;
    }
    this.removeAudio();

    const file = event?.target?.files[0];
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = () => (this.imageUrl = reader.result as string);
    reader.readAsDataURL(file);
    this.scrollToBottom();

    // return this.facade.updatePicture(file);
  }

  get isContentNotEmpty() {
    return (
      (this.messageContent && this.messageContent.length > 0) ||
      this.audioUrl ||
      this.imageUrl
    );
  }

  get isAttachment() {
    return this.imageUrl || this.audioUrl;
  }

  removeImage() {
    this.imageUrl = undefined;
  }

  removeAudio() {
    this.audioUrl = undefined;
    this.audioBlob = undefined;
  }

  removeText() {
    this.messageContent = undefined;
  }

  recordAudio() {
    if (this.mode === 'dietitian') {
      this.audioRecordingService.startTimer().then(() => {
        this.stopAudioRecord().then();
      });
      this.removeAudio();
      this.removeImage();
      this.audioRecordingService.startRecording().then();
    }
  }

  async stopAudioRecord() {
    if (this.mode === 'dietitian') {
      const blob = await this.audioRecordingService.stopRecording();
      this.audioUrl = URL.createObjectURL(blob);
      this.audioBlob = blob;
      this.scrollToBottom();
    }
  }

  convertDate(date: string) {
    return moment(date, 'YYYY-MM-DD').toDate();
  }

  async send() {
    if (!this.conversation || !this.dietitian || !this.conversation.patient) {
      return;
    }
    this.sending = true;

    let messageData: MessageData;
    if (this.mode === 'dietitian') {
      messageData = {
        conversationId: this.conversation.conversationId.id,
        recipientId: this.conversation.patient.profileId.id,
        userId: this.dietitian.userId.id,
        senderId: this.dietitian.dietitianId.id,
        conversationProps: {
          ...this.conversation.props,
          dietitian: Profile.create(
            {
              ...this.conversation.dietitian.props,
              hasNewMessage: false,
              lastMessageReadDate: new Date(),
            },
            this.conversation.dietitian.profileId.id,
          ),
          patient: Profile.create(
            {
              ...this.conversation.patient.props,
              hasNewMessage: true,
            },
            this.conversation.patient.profileId.id,
          ),
          updatedAt: new Date(),
        } as ConversationProps,
      };
    } else {
      messageData = {
        conversationId: this.conversation.conversationId.id,
        userId: this.conversation.patient.userId!.id,
        recipientId: this.dietitian.dietitianId.id,
        senderId: this.conversation.patient.profileId.id,
        conversationProps: {
          ...this.conversation.props,
          dietitian: Profile.create(
            {
              ...this.conversation.dietitian.props,
              hasNewMessage: true,
            },
            this.conversation.dietitian.profileId.id,
          ),
          patient: Profile.create(
            {
              ...this.conversation.patient.props,
              hasNewMessage: false,
              lastMessageReadDate: new Date(),
            },
            this.conversation.patient.profileId.id,
          ),
          updatedAt: new Date(),
        } as ConversationProps,
      };
    }

    if (this.imageUrl) {
      await this.facade.sendPicture(messageData, this.imageUrl);
      this.removeImage();
    } else if (this.audioBlob) {
      await this.facade.sendAudio(messageData, this.audioBlob);
      this.removeAudio();
    } else if (this.messageContent) {
      await this.facade.sendMessage(messageData, this.messageContent);
      this.removeText();
    }

    this.messageContent = undefined;
    this.cacheMessage(this.conversation, undefined)
      .then(() => (this.sending = false))
      .catch(() => (this.sending = false));
  }

  onCloseConversation() {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Marquer comme lu ?',
        subtitle: 'Voulez-vous vraiment marquer la conversation comme lu ?',
      },
    };
    this.bsModalRef = this.modalService.show(
      confirmationModalComponent.ConfirmationModalComponent,
      initialState,
    );
    if (
      this.bsModalRef.content instanceof
      confirmationModalComponent.ConfirmationModalComponent
    ) {
      this.bsModalRef.content.validate.subscribe(() => {
        this.closeConversation().then();
      });
    }
  }

  async closeConversation() {
    if (!this.conversation || !this.conversation.patient) {
      return;
    }

    this.closingConversation = true;

    if (this.mode === 'dietitian') {
      this.conversation = await this.facade.saveConversation(
        {
          ...this.conversation.props,
          dietitian: Profile.create(
            {
              ...this.conversation.dietitian.props,
              hasNewMessage: false,
              lastMessageReadDate: new Date(),
            },
            this.conversation.dietitian.id,
          ),
          patient: Profile.create(
            {
              ...this.conversation.patient.props,
              hasNewMessage: false,
            },
            this.conversation.patient.id,
          ),
        } as ConversationProps,
        this.conversation.conversationId.id.toString(),
        true,
        true,
      );
    } else {
      this.conversation = await this.facade.saveConversation(
        {
          ...this.conversation.props,
          dietitian: Profile.create(
            {
              ...this.conversation.dietitian.props,
              hasNewMessage: false,
            },
            this.conversation.dietitian.id,
          ),
          patient: Profile.create(
            {
              ...this.conversation.patient.props,
              hasNewMessage: false,
              lastMessageReadDate: new Date(),
            },
            this.conversation.patient.id,
          ),
        } as ConversationProps,
        this.conversation.conversationId.id.toString(),
        true,
        true,
      );
    }
    this.closingConversation = false;
  }

  private async loadCachedMessage(
    conversation: Conversation | undefined,
  ): Promise<CachedMessage | undefined> {
    if (conversation && this.mode === 'dietitian') {
      return this.facade.loadCachedMessage(
        conversation?.dietitian.profileId.id.toString(),
        conversation?.patient?.profileId.id.toString(),
        conversation.id.toString(),
      );
    }
    return undefined;
  }

  private async cacheMessage(
    conversation: Conversation | undefined,
    message: string | undefined,
  ): Promise<Cache | undefined> {
    if (conversation && this.mode === 'dietitian') {
      return this.facade.cacheMessage(
        conversation?.dietitian.profileId.id.toString(),
        conversation?.patient?.profileId.id.toString(),
        conversation.id.toString(),
        message,
      );
    }
    return undefined;
  }
}
